import React, { useState } from "react";
import { styled, Box } from "@mui/material";
import { keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Dividend,
  useListAndSearchAllDividends,
  useListAndSearchAllBookings,
} from "@bn/api/apiv1";
import { AutoTable, LoadingPlaceHolder } from "@components";
import { BOOKINGS_COMPATIBILITY_VERSION } from "@constants";
import { EditBookingModal } from "./InvestmentTable/InvestmentDetailModal/EditBookingModal";
import { useDividendsTableConfig } from "./useDividendsTableConfig";
import { DividendsTableHeader } from "./DividendsTableHeader";

// Note: the API returns oldest-first, we want newest-first.
// `dividendPerShare` and `grossAmount` are denominated in the investment's
// currency, so they are labelled with it — `Dividend.currency` is the dividend
// currency, which is a different field.
const dataConverter = (
  dividends: Dividend[] | undefined,
  currency: string
): Dividend[] =>
  [...(dividends ?? [])].reverse().map(
    (d) =>
      ({
        bookingId: d.bookingId,
        date: d.date ?? "",
        currency,
        dividendPerShare: d.dividendPerShare ?? 0,
        numberOfLots: d.numberOfLots ?? 0,
        grossAmount: d.grossAmount ?? 0,
      }) as Dividend
  );

const Container = styled("div")`
  padding-top: ${({ theme }) => theme.spacing(4)};
`;

interface Props {
  investmentId: number;
  currency?: string;
  image?: string | null;
}

export const DividendsTableContainer = ({
  investmentId,
  currency = "EUR",
  image,
}: Props) => {
  const { t } = useTranslation();
  const [splitAdjust, setSplitAdjust] = useState(true);
  const [editingBookingId, setEditingBookingId] = useState<number>();

  const { data: dividendsData, isLoading: isDividendsLoading } =
    useListAndSearchAllDividends(
      {
        investmentId: [investmentId],
        excludeImported: false,
        splitAdjust,
        includeManuallyEnteredDividends: true,
      },
      {
        placeholderData: keepPreviousData,
      }
    );

  // The dividends endpoint only hands back a bookingId, but the edit modal
  // needs the booking itself, so fetch just that one on demand.
  const { data: editingBookingData } = useListAndSearchAllBookings(
    {
      id: editingBookingId ? [editingBookingId] : undefined,
      enumCompatibilityVersion: BOOKINGS_COMPATIBILITY_VERSION,
    },
    { enabled: editingBookingId !== undefined }
  );
  const editingBooking = editingBookingData?.bookings?.[0];

  const convertedDividends = dataConverter(
    dividendsData?.dividends as Dividend[],
    currency
  );
  const unreceivedDividendsCount = convertedDividends.filter(
    ({ bookingId, numberOfLots = 0 }) => !bookingId && numberOfLots > 0
  ).length;

  const dividendsTableConfig = useDividendsTableConfig({
    investmentId,
    onEditDividend: setEditingBookingId,
  });

  return (
    <Container>
      <DividendsTableHeader
        unreceivedDividendsCount={unreceivedDividendsCount}
        investmentId={investmentId}
        splitAdjust={splitAdjust}
        onToggleSplitAdjust={() => setSplitAdjust((prev) => !prev)}
      />
      {isDividendsLoading ? (
        <Box mt={6}>
          <LoadingPlaceHolder />
        </Box>
      ) : (
        <AutoTable
          settings={dividendsTableConfig}
          data={convertedDividends}
          isPending={isDividendsLoading}
          showNoData={convertedDividends.length === 0}
          noDataFirstLine={t("investment.dividends.noDividends")}
        />
      )}

      <EditBookingModal
        booking={editingBooking}
        investmentId={investmentId}
        image={image}
        isOpen={Boolean(editingBooking)}
        onClose={() => setEditingBookingId(undefined)}
        onSuccess={() => setEditingBookingId(undefined)}
      />
    </Container>
  );
};
